import { cn } from "@/utils";
import type { ReactNode, SVGProps } from "react";

/**
 * Ícones do Hermes: SVG próprio, 24x24, traço `currentColor`.
 *
 * Regra do davincii: **outlined quando inativo, filled quando ativo**. O
 * `filled` só troca o preenchimento do contorno principal, com transição de
 * 300ms. O hover (`group-hover`) é animado e funciona dentro de qualquer pai
 * com a classe `group`; `key` no ícone reinicia o `animate-hermes-icon-pop`.
 */
export type HermesIconProps = Omit<SVGProps<SVGSVGElement>, "children"> & {
  filled?: boolean;
  /** Toca o "pop" ao ficar ativo. Use junto de `key={ativo ? "on" : "off"}`. */
  pop?: boolean;
  /** Animação de hover: `lift` (padrão) ou `tilt` (só gira, sem sair do lugar). */
  motion?: keyof typeof HOVER;
};

/**
 * `lift` sobe e cresce um pouco (padrão). `tilt` fica **no mesmo lugar** e só
 * gira suavemente — é o que serve a ícone posicionado por `translate` (a lupa
 * do campo): `translate` do hover sobrescreveria o do posicionamento e o
 * ícone sairia do lugar.
 */
const HOVER = {
  lift: "transition-transform duration-200 ease-out motion-reduce:transition-none group-hover:-translate-y-0.5 group-hover:scale-110",
  tilt: "transition-transform duration-300 ease-out motion-reduce:transition-none group-hover:-rotate-12",
  /** Só avança um pouco para a direita: serve a seta que aponta para onde o clique leva (a seta é girada por `rotate`, que não conflita). */
  nudge:
    "transition-transform duration-200 ease-out motion-reduce:transition-none group-hover:translate-x-0.5",
  /** Como o `nudge`, para a esquerda (seta de voltar/anterior). */
  nudgeBack:
    "transition-transform duration-200 ease-out motion-reduce:transition-none group-hover:-translate-x-0.5",
} as const;

type BaseProps = HermesIconProps & {
  /** O contorno que recebe o preenchimento quando `filled`. */
  shape: ReactNode;
  /** Detalhes que só aparecem em traço (linhas internas). */
  detail?: ReactNode;
};

const Base = ({
  filled,
  pop,
  motion = "lift",
  shape,
  detail,
  className,
  ...props
}: BaseProps) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.9}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className={cn(
      "size-4 shrink-0",
      HOVER[motion],
      pop && "animate-hermes-icon-pop",
      className
    )}
    {...props}
  >
    <g
      className={cn(
        "transition-[fill] duration-300 ease-out",
        filled ? "fill-current" : "fill-transparent"
      )}
    >
      {shape}
    </g>
    {detail}
  </svg>
);

export const HermesSearchIcon = (props: HermesIconProps) => (
  <Base
    {...props}
    shape={<circle cx="11" cy="11" r="6.5" />}
    detail={<path d="m20 20-4.2-4.2" />}
  />
);

export const HermesClockIcon = (props: HermesIconProps) => (
  <Base
    {...props}
    shape={<circle cx="12" cy="12" r="9" />}
    detail={
      <path
        d="M12 7.5V12l3 2"
        className={props.filled ? "stroke-(--hermes-bg)" : undefined}
      />
    }
  />
);

export const HermesCloseIcon = (props: HermesIconProps) => (
  <Base {...props} shape={null} detail={<path d="m6 6 12 12M18 6 6 18" />} />
);

export const HermesCopyIcon = (props: HermesIconProps) => (
  <Base
    {...props}
    shape={<rect x="9" y="9" width="11" height="11" rx="2.5" />}
    detail={
      <path d="M15 9V6.5A2.5 2.5 0 0 0 12.5 4h-6A2.5 2.5 0 0 0 4 6.5v6A2.5 2.5 0 0 0 6.5 15H9" />
    }
  />
);

export const HermesCheckIcon = (props: HermesIconProps) => (
  <Base {...props} shape={null} detail={<path d="m5 12.5 4.5 4.5L19 7.5" />} />
);

export const HermesChevronIcon = (props: HermesIconProps) => (
  <Base {...props} shape={null} detail={<path d="m6 9 6 6 6-6" />} />
);

export const HermesRefreshIcon = (props: HermesIconProps) => (
  <Base
    {...props}
    shape={null}
    detail={<path d="M20 11a8 8 0 1 0-2.3 5.7M20 5v6h-6" />}
  />
);

export const HermesBoltIcon = (props: HermesIconProps) => (
  <Base
    {...props}
    shape={<path d="M13 2.5 4.5 13.5H11l-1 8 8.5-11H12l1-8z" />}
  />
);

export const HermesAlertIcon = (props: HermesIconProps) => (
  <Base
    {...props}
    shape={<circle cx="12" cy="12" r="9" />}
    detail={<path d="M12 7.5v5.5M12 16.5h.01" />}
  />
);

export const HermesInfoIcon = (props: HermesIconProps) => (
  <Base
    {...props}
    shape={<circle cx="12" cy="12" r="9" />}
    detail={<path d="M12 11v5.5M12 7.5h.01" />}
  />
);

export const HermesArrowLeftIcon = (props: HermesIconProps) => (
  <Base {...props} shape={null} detail={<path d="M19 12H5m6-6-6 6 6 6" />} />
);

export const HermesPhoneIcon = (props: HermesIconProps) => (
  <Base
    {...props}
    shape={<rect x="7" y="2.5" width="10" height="19" rx="2.5" />}
    detail={<path d="M11 18.5h2" />}
  />
);

export const HermesFilterIcon = (props: HermesIconProps) => (
  <Base {...props} shape={<path d="M4 5h16l-6 7.2V19l-4-2v-4.8L4 5z" />} />
);

export const HermesSendIcon = (props: HermesIconProps) => (
  <Base
    {...props}
    shape={<path d="M21 3 3 10.5l7 3 3 7L21 3z" />}
    detail={<path d="m10 13.5 4-4" />}
  />
);

/** Dois vistos: entregue; o mesmo ícone, colorido, é lido. */
export const HermesCheckCheckIcon = (props: HermesIconProps) => (
  <Base
    {...props}
    shape={null}
    detail={<path d="m2.5 12.5 4.5 4.5L15 8.5M10.5 15.5l1.5 1.5L21 7.5" />}
  />
);

export const HermesMessageIcon = (props: HermesIconProps) => (
  <Base
    {...props}
    shape={
      <path d="M5 4h14a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-7l-5 4v-4H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" />
    }
    detail={
      <path
        d="M8 9h8M8 12.5h5"
        className={props.filled ? "stroke-(--hermes-bg)" : undefined}
      />
    }
  />
);

/** Cursor de clique: o toque num botão da mensagem. */
export const HermesPointerIcon = (props: HermesIconProps) => (
  <Base
    {...props}
    shape={<path d="m6 3 12 8.5-5.2 1.2L16 19l-2.6 1.2-3.1-6.3L6 17.5V3z" />}
  />
);

export const HermesImageIcon = (props: HermesIconProps) => (
  <Base
    {...props}
    shape={<rect x="3" y="4" width="18" height="16" rx="3" />}
    detail={
      <path
        d="m3.5 16 5-5 4 4 2.5-2.5 5.5 5M8.5 9h.01"
        className={props.filled ? "stroke-(--hermes-bg)" : undefined}
      />
    }
  />
);

export const HermesFileIcon = (props: HermesIconProps) => (
  <Base
    {...props}
    shape={
      <path d="M6 3h8l5 5v11a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z" />
    }
    detail={
      <path
        d="M14 3v5h5M8 13h8M8 16.5h5"
        className={props.filled ? "stroke-(--hermes-bg)" : undefined}
      />
    }
  />
);

export const HermesTrashIcon = (props: HermesIconProps) => (
  <Base
    {...props}
    shape={<path d="M6 7.5h12l-.9 11a2 2 0 0 1-2 1.8H8.9a2 2 0 0 1-2-1.8z" />}
    detail={
      <path d="M4 7.5h16M9.5 7.5V5.2a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1v2.3" />
    }
  />
);

/** Moeda: o que é cobrado. */
export const HermesCoinIcon = (props: HermesIconProps) => (
  <Base
    {...props}
    shape={<circle cx="12" cy="12" r="9" />}
    detail={
      <path
        d="M12 6.5v11M14.6 9.4c-.4-.9-1.4-1.4-2.6-1.4-1.5 0-2.6.7-2.6 1.9 0 2.5 5.2 1.3 5.2 3.9 0 1.2-1.1 1.9-2.6 1.9-1.3 0-2.3-.5-2.7-1.5"
        className={props.filled ? "stroke-(--hermes-bg)" : undefined}
      />
    }
  />
);

/** Sino: aviso de utilidade (a mensagem que o cliente espera receber). */
export const HermesBellIcon = (props: HermesIconProps) => (
  <Base
    {...props}
    shape={<path d="M6 16.5V11a6 6 0 1 1 12 0v5.5l1.5 2h-15l1.5-2z" />}
    detail={<path d="M10 20.8a2.2 2.2 0 0 0 4 0" />}
  />
);

/** Megafone: campanha de marketing. */
export const HermesMegaphoneIcon = (props: HermesIconProps) => (
  <Base
    {...props}
    shape={<path d="M4 9.5v5h3.5l7.5 4.5V5L7.5 9.5H4z" />}
    detail={<path d="M18 9.5a3.5 3.5 0 0 1 0 5M7.5 14.5l1 5h2.2" />}
  />
);

/** Chave: código de autenticação. */
export const HermesKeyIcon = (props: HermesIconProps) => (
  <Base
    {...props}
    shape={<circle cx="8" cy="15.5" r="4" />}
    detail={<path d="m11 12.5 8-8M16.5 7l2.5 2.5M14 9.5l2 2" />}
  />
);

/** Elo de corrente: botão que abre um link. */
export const HermesLinkIcon = (props: HermesIconProps) => (
  <Base
    {...props}
    shape={null}
    detail={
      <path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1" />
    }
  />
);

/** Seta de resposta: botão de resposta rápida. */
export const HermesReplyIcon = (props: HermesIconProps) => (
  <Base
    {...props}
    shape={null}
    detail={<path d="M9 7 4 12l5 5M4 12h10a6 6 0 0 1 6 6v1" />}
  />
);

/** Câmera de vídeo: cabeçalho de vídeo. */
export const HermesVideoIcon = (props: HermesIconProps) => (
  <Base
    {...props}
    shape={<rect x="3" y="6" width="12" height="12" rx="2.5" />}
    detail={<path d="m15 10.5 6-3.5v10l-6-3.5" />}
  />
);

/** Marcador de mapa: cabeçalho de localização. */
export const HermesMapPinIcon = (props: HermesIconProps) => (
  <Base
    {...props}
    shape={<path d="M12 21s7-6.2 7-11.5A7 7 0 0 0 5 9.5C5 14.8 12 21 12 21z" />}
    detail={<circle cx="12" cy="9.5" r="2.4" />}
  />
);

/** Recibo: a cobrança que originou um envio. */
export const HermesReceiptIcon = (props: HermesIconProps) => (
  <Base
    {...props}
    shape={
      <path d="M6 3.5h12v17l-2.4-1.6-2.1 1.6-2.1-1.6-2.1 1.6-2.4-1.6-.9.6z" />
    }
    detail={
      <path
        d="M9 8.5h6M9 12h6M9 15.5h3"
        className={props.filled ? "stroke-(--hermes-bg)" : undefined}
      />
    }
  />
);

/** Olho: ver o detalhe. Cheio, a pupila vira o furo. */
export const HermesEyeIcon = (props: HermesIconProps) => (
  <Base
    {...props}
    shape={
      <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" />
    }
    detail={
      <circle
        cx="12"
        cy="12"
        r="2.6"
        className={props.filled ? "stroke-(--hermes-bg)" : undefined}
      />
    }
  />
);

/** Lápis: editar. Cheio, o corpo do lápis é preenchido; no hover ele gira sem sair do lugar. */
export const HermesPencilIcon = ({
  motion = "tilt",
  ...props
}: HermesIconProps) => (
  <Base
    {...props}
    motion={motion}
    shape={<path d="M4 20l1-4.2L16.4 4.4a2 2 0 0 1 2.8 0l.4.4a2 2 0 0 1 0 2.8L8.2 19z" />}
    detail={<path d="M14.5 6.3l3.2 3.2M4 20l4.2-1" />}
  />
);

/** A marca da Meta (o laço infinito), em traço como os demais ícones do kit. */
export const HermesMetaIcon = (props: HermesIconProps) => (
  <Base
    {...props}
    shape={
      <path d="M12 12c-1.6-2.5-3.3-4.5-5.3-4.5a4.5 4.5 0 0 0 0 9c2 0 3.7-2 5.3-4.5zm0 0c1.6 2.5 3.3 4.5 5.3 4.5a4.5 4.5 0 0 0 0-9c-2 0-3.7 2-5.3 4.5z" />
    }
  />
);
